import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { MediaUpload } from "../../components/admin/MediaUpload";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyService = { slug: "", name_pt: "", name_fr: "", category: "", description_pt: "", description_fr: "", price: 0, icon: "Sparkles", image_url: "", active: true, order: 0 };
const emptyPacote = { service_id: "", name_pt: "", name_fr: "", sessions_count: 5, total_price: 0, description_pt: "", description_fr: "", active: true };

const NEW_CAT = "__new__";

const CategoryField = ({ categories, value, onChange }) => {
  const [creating, setCreating] = useState(false);
  const isNew = creating || (value && !categories.includes(value));
  return (
    <div>
      <Label>Categoria</Label>
      <Select value={isNew ? NEW_CAT : value} onValueChange={(v) => { setCreating(v === NEW_CAT); onChange(v === NEW_CAT ? "" : v); }}>
        <SelectTrigger data-testid="service-form-category"><SelectValue placeholder="Escolher uma categoria" /></SelectTrigger>
        <SelectContent>
          {categories.map((c) => <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>)}
          <SelectItem value={NEW_CAT} data-testid="category-option-new">+ Nova categoria</SelectItem>
        </SelectContent>
      </Select>
      {isNew && <Input className="mt-2" required autoFocus placeholder="Nome da nova categoria" value={value} onChange={(e) => onChange(e.target.value)} data-testid="service-form-category-new" />}
    </div>
  );
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [openService, setOpenService] = useState(false);
  const [openPacote, setOpenPacote] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [pacoteForm, setPacoteForm] = useState(emptyPacote);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/admin/pacotes").then((r) => setPacotes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const categories = [...new Set(services.map((s) => s.category).filter(Boolean))].sort();

  const editService = (s) => { setServiceForm(s); setEditingId(s.id); setOpenService(true); };
  const newService = () => { setServiceForm(emptyService); setEditingId(null); setOpenService(true); };

  const submitService = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/services/${editingId}`, serviceForm);
      else await api.post("/admin/services", serviceForm);
      toast.success("Serviço salvo!");
      setOpenService(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editPacote = (p) => { setPacoteForm(p); setEditingId(p.id); setOpenPacote(true); };
  const newPacote = () => { setPacoteForm(emptyPacote); setEditingId(null); setOpenPacote(true); };

  const submitPacote = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/pacotes/${editingId}`, pacoteForm);
      else await api.post("/admin/pacotes", pacoteForm);
      toast.success("Pacote salvo!");
      setOpenPacote(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-services-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Serviços & Pacotes</h1>
      <Tabs defaultValue="servicos">
        <TabsList>
          <TabsTrigger value="servicos" data-testid="tab-servicos">Serviços</TabsTrigger>
          <TabsTrigger value="pacotes" data-testid="tab-pacotes">Pacotes</TabsTrigger>
        </TabsList>

        <TabsContent value="servicos">
          <div className="flex justify-end mb-4"><Button onClick={newService} data-testid="new-service-button" className="bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors"><Plus className="w-4 h-4 mr-1" /> Novo Serviço</Button></div>
          <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase"><tr><th className="text-left px-4 py-3">Nome (PT)</th><th className="text-left px-4 py-3">Categoria</th><th className="text-left px-4 py-3">Preço</th><th className="text-left px-4 py-3">Ativo</th><th></th></tr></thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} className="border-t border-[#9B6179]/5" data-testid={`service-row-${s.slug}`}>
                    <td className="px-4 py-3">{s.name_pt}</td><td className="px-4 py-3">{s.category}</td><td className="px-4 py-3">{s.price}€</td>
                    <td className="px-4 py-3">{s.active ? "Sim" : "Não"}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => editService(s)} data-testid={`edit-service-${s.slug}`}><Pencil className="w-4 h-4 text-[#6F3F55]" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pacotes">
          <div className="flex justify-end mb-4"><Button onClick={newPacote} data-testid="new-pacote-button" className="bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors"><Plus className="w-4 h-4 mr-1" /> Novo Pacote</Button></div>
          <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase"><tr><th className="text-left px-4 py-3">Nome (PT)</th><th className="text-left px-4 py-3">Sessões</th><th className="text-left px-4 py-3">Preço Total</th><th></th></tr></thead>
              <tbody>
                {pacotes.map((p) => (
                  <tr key={p.id} className="border-t border-[#9B6179]/5" data-testid={`pacote-row-${p.id}`}>
                    <td className="px-4 py-3">{p.name_pt}</td><td className="px-4 py-3">{p.sessions_count}</td><td className="px-4 py-3">{p.total_price}€</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => editPacote(p)} data-testid={`edit-pacote-${p.id}`}><Pencil className="w-4 h-4 text-[#6F3F55]" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={openService} onOpenChange={setOpenService}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="service-modal">
          <form onSubmit={submitService} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">{editingId ? "Editar" : "Novo"} Serviço</p>
            <div><Label>Slug</Label><Input required value={serviceForm.slug} onChange={(e) => setServiceForm({ ...serviceForm, slug: e.target.value })} data-testid="service-form-slug" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome (PT)</Label><Input required value={serviceForm.name_pt} onChange={(e) => setServiceForm({ ...serviceForm, name_pt: e.target.value })} data-testid="service-form-name-pt" /></div>
              <div><Label>Nome (FR)</Label><Input required value={serviceForm.name_fr} onChange={(e) => setServiceForm({ ...serviceForm, name_fr: e.target.value })} data-testid="service-form-name-fr" /></div>
            </div>
            <CategoryField categories={categories} value={serviceForm.category} onChange={(v) => setServiceForm({ ...serviceForm, category: v })} />
            <div><Label>Descrição (PT)</Label><Textarea value={serviceForm.description_pt} onChange={(e) => setServiceForm({ ...serviceForm, description_pt: e.target.value })} data-testid="service-form-desc-pt" /></div>
            <div><Label>Descrição (FR)</Label><Textarea value={serviceForm.description_fr} onChange={(e) => setServiceForm({ ...serviceForm, description_fr: e.target.value })} data-testid="service-form-desc-fr" /></div>
            <div className="space-y-2"><Label>Imagem</Label>
              {serviceForm.image_url && <img src={serviceForm.image_url} alt="" className="h-24 rounded-[2px] object-cover" data-testid="service-form-image-preview" />}
              <MediaUpload folder="services" onUploaded={(url) => setServiceForm((f) => ({ ...f, image_url: url }))} testid="service-form-image-upload" label="Enviar imagem" />
              <Input placeholder="ou cole uma URL" value={serviceForm.image_url} onChange={(e) => setServiceForm({ ...serviceForm, image_url: e.target.value })} data-testid="service-form-image" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preço (€)</Label><Input type="number" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: Number(e.target.value) })} data-testid="service-form-price" /></div>
              <div><Label>Ordem</Label><Input type="number" value={serviceForm.order} onChange={(e) => setServiceForm({ ...serviceForm, order: Number(e.target.value) })} data-testid="service-form-order" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={serviceForm.active} onCheckedChange={(v) => setServiceForm({ ...serviceForm, active: v })} data-testid="service-form-active" /><Label>Ativo no site</Label></div>
            <Button type="submit" className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="service-form-submit">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openPacote} onOpenChange={setOpenPacote}>
        <DialogContent data-testid="pacote-modal">
          <form onSubmit={submitPacote} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">{editingId ? "Editar" : "Novo"} Pacote</p>
            <div>
              <Label>Serviço vinculado</Label>
              <Select value={pacoteForm.service_id} onValueChange={(v) => setPacoteForm({ ...pacoteForm, service_id: v })}>
                <SelectTrigger data-testid="pacote-form-service"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name_pt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome (PT)</Label><Input required value={pacoteForm.name_pt} onChange={(e) => setPacoteForm({ ...pacoteForm, name_pt: e.target.value })} data-testid="pacote-form-name-pt" /></div>
              <div><Label>Nome (FR)</Label><Input required value={pacoteForm.name_fr} onChange={(e) => setPacoteForm({ ...pacoteForm, name_fr: e.target.value })} data-testid="pacote-form-name-fr" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sessões</Label><Input type="number" value={pacoteForm.sessions_count} onChange={(e) => setPacoteForm({ ...pacoteForm, sessions_count: Number(e.target.value) })} data-testid="pacote-form-sessions" /></div>
              <div><Label>Preço Total (€)</Label><Input type="number" value={pacoteForm.total_price} onChange={(e) => setPacoteForm({ ...pacoteForm, total_price: Number(e.target.value) })} data-testid="pacote-form-price" /></div>
            </div>
            <Button type="submit" className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="pacote-form-submit">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
